export const COWORK_SCENARIOS = {
  cowork_clip1: {
    question:
      'Which business units are growing or shrinking this year based on hires minus terminations, and what is the monthly trend?',
    intro:
      "Here's the 12-month workforce summary showing hires, terminations, net change, and ending headcount (July 2025 to June 2026):",
    table: {
      title: 'Monthly Hires, Terminations, Net Change & Ending Headcount',
      columns: ['Month', 'Hires', 'Terms', 'Net', 'Headcount'],
      rows: [
        ['2025-07', '228', '166', '+62', '20,109'],
        ['2025-08', '241', '172', '+69', '20,178'],
        ['2025-09', '219', '158', '+61', '20,239'],
        ['2025-10', '205', '171', '+34', '20,273'],
        ['2025-11', '198', '163', '+35', '20,308'],
        ['2025-12', '214', '188', '+26', '21,305'],
      ],
      footer: 'Show 6 more',
    },
    observations: [
      {
        lead: 'H2 2025 was a growth period',
        body: 'Hires exceeded terminations, adding +281 net employees (Jul-Dec), with headcount peaking at 21,305 in December 2025.',
      },
      {
        lead: '2026 shifted to contraction',
        body: 'Net change flipped negative from January onward as hiring slowed while terminations moderated.',
      },
    ],
    followUps: [
      'Which business units drove the largest net headcount gains in H2 2025?',
      'How does the monthly net change trend compare to the same period last year?',
    ],
    steps: ['question', 'querying', 'retrieved', 'intro', 'content', 'insights', 'followups'],
    stepDelays: [0, 450, 1000, 1400, 1800, 2800, 4000],
    loopPause: 6800,
  },

  cowork_clip2: {
    question:
      'Which business units have the highest termination pressure relative to their current headcount?',
    intro:
      "Here's the YTD termination pressure ranked by termination rate (terminations as a % of current active headcount):",
    table: {
      title: 'YTD Termination Rate by Business Unit (Jan-Jun 2026)',
      columns: ['Business unit', 'Active', 'YTD terms', 'Rate'],
      rows: [
        ['Quality Management', '546', '20', '3.7%'],
        ['Customer Success', '183', '6', '3.3%'],
        ['International Operations', '457', '15', '3.3%'],
        ['Data & AI Platforms', '537', '16', '3.0%'],
        ['Manufacturing Operations', '858', '26', '3.0%'],
        ['Sales & Commercial Opera', '1,152', '32', '2.8%'],
      ],
      footer: 'Show 5 more',
    },
    chart: {
      title: 'YTD Termination Rate by Business Unit (% of Current Headcount)',
      legend: 'Termination rate %',
      min: 1.8,
      max: 3.7,
      rows: [
        { label: 'Quality Management', value: 3.7 },
        { label: 'Customer Success', value: 3.3 },
        { label: 'International Operations', value: 3.3 },
        { label: 'Data & AI Platforms', value: 3.0 },
        { label: 'Manufacturing Operations', value: 3.0 },
        { label: 'Sales & Commercial Opera', value: 2.8 },
      ],
    },
    takeaways: [
      {
        lead: 'Quality Management',
        body: 'faces the highest termination pressure at 3.7% of its headcount lost YTD, with only 546 active employees, 20 departures hit proportionally harder.',
      },
      {
        lead: 'Customer Success',
        body: '(3.3%) and International Operations (3.3%) follow closely, both smaller units where even modest termination volumes create outsized impact.',
      },
      {
        lead: 'Corporate Shared Services',
        body: 'carries the highest absolute termination volume (99 terms) but a relatively lower rate (2.4%) due to large headcount.',
      },
    ],
    summary:
      'The organization-wide YTD rate sits around ~2.5% (499 terminations across ~20,983 employees), annualizing to ~5.2%. Units above 3% may warrant retention focus.',
    followUps: [
      'What are the top termination reasons across the business units with the highest termination rates?',
      'How does the termination rate by business unit compare to the same period last year?',
    ],
    steps: ['question', 'querying', 'retrieved', 'intro', 'table', 'chart', 'takeaways', 'summary', 'followups'],
    stepDelays: [0, 500, 1100, 1500, 1900, 2800, 4000, 5200, 6400],
    loopPause: 7500,
  },

  cowork_clip3: {
    question:
      'Compare voluntary and involuntary terminations by business unit over the trailing 12 months.',
    intro:
      "Here's the breakdown of voluntary vs. involuntary terminations across all business units over the trailing 12 months:",
    insights: {
      title: 'Key insights',
      sections: [
        {
          heading: 'Voluntary dominates at ~74%',
          bullets: [
            'Voluntary exits account for roughly three-quarters of all terminations organization-wide.',
            'Robotics & Automation, Manufacturing Operations, and Corporate Shared Services show the highest voluntary share.',
          ],
        },
        {
          heading: 'Monthly patterns',
          bullets: [
            'H2 2025 saw elevated totals (150-188/month) with voluntary peaks in December 2025.',
            '2026 volumes are lower (81-121/month) with a stable ~75/25 voluntary/involuntary ratio.',
          ],
        },
      ],
    },
    summary:
      'Retention programs should focus on the voluntary side, especially in Robotics & Automation, Manufacturing Operations, Corporate Shared Services, and Cloud Services.',
    followUps: [
      'What are the top termination reasons for voluntary exits by business unit?',
      'How does the voluntary termination rate compare across job levels?',
    ],
    steps: ['question', 'querying', 'retrieved', 'intro', 'insights', 'summary', 'followups'],
    stepDelays: [0, 450, 1000, 1400, 1800, 3200, 4200],
    loopPause: 7000,
  },

  cowork_clip4: {
    question:
      'Which departments have high termination counts but also continued hiring activity this year?',
    intro: 'Departments with active backfill patterns vs. managed contraction this year:',
    insights: {
      title: 'Key patterns',
      sections: [
        {
          heading: 'Highest churn with active backfilling',
          bullets: [
            'Cloud Services: 50 terminations and 26 hires, the most active replacement effort.',
            'Accounting: 13 hires vs. 25 terminations.',
            'Legal & Compliance: 11 hires against 24 terminations.',
          ],
        },
        {
          heading: 'High terminations with minimal replacement',
          bullets: [
            'Enterprise Applications: 35 terminations but only 10 hires.',
            'R&D Programs: 33 departures vs. 9 hires.',
            'Commercial Operations: 27 terminations with only 6 hires.',
          ],
        },
      ],
    },
    summary:
      'Interpretation: Cloud Services and Accounting are in replace-and-maintain mode, while Enterprise Applications and R&D Programs reflect deliberate shrinkage.',
    followUps: [
      'Which departments have zero hiring activity despite terminations this year?',
      'What is the backfill rate by department compared to last year?',
    ],
    steps: ['question', 'querying', 'retrieved', 'intro', 'insights', 'summary', 'followups'],
    stepDelays: [0, 450, 1000, 1400, 1800, 3400, 4400],
    loopPause: 7200,
  },

  cowork_clip5: {
    question:
      'Where are we seeing the largest gap between planned headcount and actual active headcount by month?',
    intro: 'Hiring gap analysis by legal entity: current vs. peak gap and open requisitions:',
    table: {
      title: 'Where the gaps are largest',
      columns: ['Company', 'Current gap', 'Peak gap', 'Open reqs'],
      rows: [
        ['MyOrgLab United States', '-703', '-801 (Dec)', '703'],
        ['MyOrgLab China', '-250', '-312 (Nov)', '250'],
        ['MyOrgLab India', '-240', '-298 (Oct)', '240'],
        ['MyOrgLab Czech Republic', '-89', '-112 (Dec)', '89'],
        ['MyOrgLab United Kingdom', '-72', '-95 (Jan)', '72'],
      ],
      footer: 'Show 3 more',
    },
    insights: {
      title: 'Interpretation',
      sections: [
        {
          bullets: [
            'United States carries 48% of the total gap (703 of 1,459 unfilled positions) with an 11.4% gap-to-plan ratio.',
            'China and India account for 34% of the gap, peaking in late 2025.',
            'Czech Republic shows the most persistent gap relative to entity size.',
          ],
        },
      ],
    },
    followUps: [
      'How has the hiring gap trend changed month over month in the United States?',
      'Which business units within the US entity drive the largest gap?',
    ],
    steps: ['question', 'querying', 'retrieved', 'intro', 'content', 'insights', 'followups'],
    stepDelays: [0, 450, 1000, 1400, 1800, 3000, 4200],
    loopPause: 7000,
  },

  cowork_clip6: {
    question:
      'Which business units have the most open requisitions, and how does that compare to recent terminations?',
    intro: 'Open requisition pipeline vs. recent termination volume by company:',
    insights: {
      title: 'Key findings',
      sections: [
        {
          heading: 'Largest open requisition pipelines',
          bullets: [
            'MyOrgLab United States: 703 open reqs, 2,690 openings remaining (16× termination volume).',
            'MyOrgLab China: 250 open reqs (1,139 openings).',
            'MyOrgLab India: 240 open reqs (914 openings).',
          ],
        },
        {
          heading: 'Demand far exceeds attrition replacement',
          bullets: [
            '5,789 total openings against 554 terminations, a 10.5× ratio globally.',
            'United Kingdom and United States have the most aggressive planned growth.',
          ],
        },
      ],
    },
    summary:
      'The disconnect: hiring slowed in 2026 (~176 total hires YTD vs. 5,789 openings). Teams must scale hiring capacity or rationalize plans.',
    followUps: [
      'Which business units have the highest open-req-to-termination ratio?',
      'How many reqs were opened vs. filled vs. cancelled each month?',
    ],
    steps: ['question', 'querying', 'retrieved', 'intro', 'insights', 'summary', 'followups'],
    stepDelays: [0, 450, 1000, 1400, 1800, 3600, 4800],
    loopPause: 7400,
  },

  cowork_clip7: {
    question:
      'Which business units should a talent manager prioritize based on high terminations, low net headcount change, and open requisition demand?',
    intro: 'Prioritized business units based on termination volume, replacement rate, and open demand:',
    tierTables: [
      {
        tier: 'Tier 2: High Priority',
        tone: 'red',
        columns: ['Business unit', 'Why prioritize'],
        rows: [
          ['IT & Security', '35 terms (net -25), only 28.6% replaced. 80% voluntary.'],
          ['Research & Development', '33 terms (net -24), only 27.3% replaced. Core innovation at risk.'],
          ['Sales & Commercial Operations', '32 terms (net -22), 31.3% replaced, 72% voluntary.'],
        ],
      },
      {
        tier: 'Tier 3: Monitor',
        tone: 'amber',
        columns: ['Business unit', 'Why prioritize'],
        rows: [
          ['Corporate Shared Services', '99 terms (net -62) but 37.4% replacement. Scale matters.'],
          ['Cloud Services', '96 terms (net -57) with 40.6% replacement, best among high-volume BUs.'],
        ],
      },
    ],
    insights: {
      title: 'Key actionable insights',
      sections: [
        {
          bullets: [
            'Retention is the #1 lever: 70-87% of terminations across all BUs are voluntary.',
            'Replacement rates are universally below 50%. No BU is replacing even half of its departures.',
          ],
        },
      ],
    },
    followUps: [
      'What retention interventions have worked in business units with improving replacement rates?',
      'Which job families within IT & Security have the highest voluntary attrition?',
    ],
    steps: ['question', 'querying', 'retrieved', 'intro', 'content', 'insights', 'followups'],
    stepDelays: [0, 450, 1000, 1400, 1800, 3400, 4600],
    loopPause: 7200,
  },

  cowork_clip8: {
    question:
      'Show headcount trend, hires, terminations, and open requisitions by month for the last 12 months.',
    intro:
      "Here's the complete workforce summary by month for the last 12 months (July 2025 to June 2026):",
    table: {
      title: 'Key dynamics at a glance',
      columns: ['Metric', 'H2 2025', '2026 YTD', 'Change'],
      rows: [
        ['Avg monthly hires', '213', '35', '-83%'],
        ['Avg monthly terminations', '166', '100', '-40%'],
        ['Monthly net change', '+47', '-65', 'Flipped negative'],
        ['Open requisitions', '1,625→1,683', '1,600→1,459', '-13%'],
        ['Headcount direction', 'Growing (+196)', 'Shrinking (-323)', 'Reversed'],
      ],
    },
    summary:
      'The organization transitioned from a growth posture in H2 2025 to managed contraction in 2026, with hiring nearly frozen while attrition continues at a moderated pace.',
    followUps: [
      'How does the headcount trend look broken down by company or country?',
      'What is the average time to fill for requisitions filled in the last 12 months?',
    ],
    steps: ['question', 'querying', 'retrieved', 'intro', 'content', 'summary', 'followups'],
    stepDelays: [0, 450, 1000, 1400, 1800, 3200, 4400],
    loopPause: 7000,
  },
};

export function getCoworkScenario(id) {
  return COWORK_SCENARIOS[id] ?? null;
}
